"use strict";
// Feature: reconciliation
//
// Pure allocation / settlement planner for the Banking-group reconciliation
// Lambda. Holds all money-critical logic — unit- and property-testable in
// isolation, no AWS deps. The Lambda's thin adapters call `planAllocation`,
// commit the result via one `TransactWriteCommand`, and translate typed
// `ReconError`s to HTTP statuses.
//
// Design refs:
//   .kiro/specs/reconciliation/design.md
//   .kiro/specs/reconciliation/tasks.md — Task 1
//   .kiro/specs/reconciliation/requirements.md
//
// Verbatim reuse of the deployed settlement legs from
// backend/src/lambdas/finance/src/handlers/ledgerPosting.ts
// (service_charge_receipt = Dr 1900 / Cr 1910; bill_payment = Dr 5001 / Cr 1900).
// New in this planner: money-in invoice_receipt (Dr 1900 / Cr 1004), transfer,
// and other_journal legs.
//
// Constitutional constraints:
//   Rule 1 — every amount is integer pence.
//   Rule 2 — every allocation posts a balanced Settlement to LEDGER_TABLE.
//   Principle II — `ownerId` + `buildingId` scoping enforced by
//     `validateAllocation` (cross-building rejected).
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildSettlementReversal = exports.planAllocation = exports.buildSettlementPosting = exports.linkFieldForItemType = exports.validateAllocation = exports.settlementKey = exports.outstandingBalance = exports.ReconciliationError = void 0;
const postingEngine_1 = require("./postingEngine");
const controlAccounts_1 = require("./controlAccounts");
/**
 * Thrown by `planAllocation` and `buildSettlementPosting` on invalid input.
 * Carries the underlying typed `ReconError` so the caller can inspect the
 * kind without string-matching.
 */
class ReconciliationError extends Error {
    constructor(detail) {
        super(detail.message);
        this.name = 'ReconciliationError';
        this.detail = detail;
        Object.setPrototypeOf(this, ReconciliationError.prototype);
    }
}
exports.ReconciliationError = ReconciliationError;
// ============================================================================
// Primitives
// ============================================================================
/**
 * Outstanding balance in pence. Always non-negative — an over-paid item
 * (which shouldn't happen in practice; postings would prevent it) is
 * clamped at zero so the planner never proposes a "negative allocation".
 */
function outstandingBalance(item) {
    return Math.max(0, item.amount - item.amountPaid);
}
exports.outstandingBalance = outstandingBalance;
/**
 * Deterministic idempotency key for a (transaction, item) pair. Matches the
 * `source.id` value the deployed `finance/ledgerPosting.ts` `buildSettlementPosting`
 * passes to `buildPosting` — namely `${itemId}#${bankTransactionId}`. The
 * shared posting engine then derives the posting group id as
 * `settlement#{itemId}#{bankTransactionId}` (via its `resolvePostingId`),
 * so repeat commits of the same pair are perfectly idempotent (same
 * `entryId`s overwrite the same rows).
 */
function settlementKey(bankTransactionId, itemId) {
    return `${itemId}#${bankTransactionId}`;
}
exports.settlementKey = settlementKey;
/**
 * Validate a proposed allocation vector. Pure; never throws. Returns a
 * discriminated result the caller maps to HTTP (all rejections → 400
 * with zero writes; the `ownerId` 403 gate is upstream).
 *
 * Rejection order (first hit wins):
 *   1. EMPTY_ALLOCATION      — no allocations, or any allocation ≤ 0
 *   2. CROSS_BUILDING        — any item's buildingId ≠ bank's buildingId
 *   3. ITEM_OVER_ALLOCATION  — any single allocation > that item's outstanding
 *   4. OVER_ALLOCATION       — Σ allocations > transactionAmount
 *
 * Every allocation must reference an item in the provided items map; an
 * allocation whose `itemId` has no matching item is treated as EMPTY_ALLOCATION
 * (the caller should have filtered these out upstream — this is a defensive
 * guard).
 */
function validateAllocation(input) {
    const { transactionAmount, bankAccount, items, allocations } = input;
    if (!Array.isArray(allocations) || allocations.length === 0) {
        return {
            ok: false,
            error: {
                kind: 'EMPTY_ALLOCATION',
                message: 'At least one allocation is required',
            },
        };
    }
    for (const a of allocations) {
        if (!Number.isInteger(a.amount) ||
            a.amount <= 0) {
            return {
                ok: false,
                error: {
                    kind: 'EMPTY_ALLOCATION',
                    message: `Allocation for item ${a.itemId} must be a positive integer of pence (got ${a.amount})`,
                },
            };
        }
    }
    const itemById = new Map();
    for (const item of items)
        itemById.set(item.itemId, item);
    // Building ring-fence and item-lookup, in that order.
    for (const a of allocations) {
        const item = itemById.get(a.itemId);
        if (!item) {
            return {
                ok: false,
                error: {
                    kind: 'EMPTY_ALLOCATION',
                    message: `Allocation references unknown item ${a.itemId}`,
                },
            };
        }
        if (item.buildingId !== bankAccount.buildingId) {
            return {
                ok: false,
                error: {
                    kind: 'CROSS_BUILDING',
                    message: `Item ${a.itemId} belongs to building ${item.buildingId}; bank account is in ${bankAccount.buildingId}`,
                    itemId: a.itemId,
                    bankBuildingId: bankAccount.buildingId,
                    itemBuildingId: item.buildingId,
                },
            };
        }
        const outstanding = outstandingBalance(item);
        if (a.amount > outstanding) {
            return {
                ok: false,
                error: {
                    kind: 'ITEM_OVER_ALLOCATION',
                    message: `Allocation of ${a.amount} exceeds item ${a.itemId}'s outstanding balance of ${outstanding}`,
                    itemId: a.itemId,
                    requested: a.amount,
                    outstanding,
                },
            };
        }
    }
    const allocated = allocations.reduce((s, a) => s + a.amount, 0);
    if (allocated > transactionAmount) {
        return {
            ok: false,
            error: {
                kind: 'OVER_ALLOCATION',
                message: `Total allocation ${allocated} exceeds transaction amount ${transactionAmount}`,
                allocated,
                transactionAmount,
            },
        };
    }
    return { ok: true };
}
exports.validateAllocation = validateAllocation;
// ============================================================================
// buildSettlementPosting — per-type balanced legs
// ============================================================================
/**
 * Choose the correct link field on the bank transaction for an outstanding
 * item's type (Req 5.5).
 */
function linkFieldForItemType(itemType) {
    switch (itemType) {
        case 'invoice':
            return 'linkedInvoiceId';
        case 'bill':
            return 'linkedBillId';
        case 'service_charge':
            // Service-charge receipts write both linkedInvoiceId and the generic
            // financialRecordId in the deployed reconcile path; the planner picks
            // the generic field so downstream queries can find the settlement
            // regardless of the reclassification history.
            return 'financialRecordId';
    }
}
exports.linkFieldForItemType = linkFieldForItemType;
/**
 * Build the balanced settlement Posting for one allocation. Amounts are
 * integer pence. Passes `source: { type: 'settlement', id: settlementKey(...) }`
 * so the resulting posting group id is deterministic and idempotent under
 * `buildPosting`'s `resolvePostingId` rule.
 *
 * Legs by type:
 *   service_charge_receipt  Dr 1900 / Cr 1910   (matches deployed service_charge path)
 *   invoice_receipt         Dr 1900 / Cr 1004   (new — money in for standard invoices)
 *   bill_payment            Dr 5001 / Cr 1900   (matches deployed bill/invoice path)
 *   transfer                Dr 1900(dest) / Cr 1900(source)
 *   other_journal           Dr/Cr chosen ↔ Cr/Dr 1900 per direction
 *
 * @throws {ReconciliationError} if `settlementAmount` is not a positive
 *         integer of pence — the underlying `buildPosting` would throw
 *         `PostingError` on this, but we wrap so callers only need to
 *         catch one error class.
 */
function buildSettlementPosting(input, now) {
    const { txnType, itemId, ownerId, buildingId, date, bankTransactionId, settlementAmount, createdBy, context, } = input;
    if (!Number.isInteger(settlementAmount) || settlementAmount <= 0) {
        throw new ReconciliationError({
            kind: 'EMPTY_ALLOCATION',
            message: `settlementAmount must be a positive integer of pence (got ${settlementAmount})`,
        });
    }
    const source = {
        type: 'settlement',
        id: settlementKey(bankTransactionId, itemId),
    };
    const description = `Settlement (${txnType}) of ${itemId}`;
    switch (txnType) {
        case 'service_charge_receipt':
            // Dr 1900 Bank / Cr 1910 Service Charge Debtors — mirrors deployed
            // finance/ledgerPosting.ts service_charge leg verbatim.
            return (0, postingEngine_1.buildPosting)({
                ownerId,
                buildingId,
                date,
                description,
                source,
                createdBy,
                lines: [
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.bank,
                        category: 'Cash at Bank',
                        debit: settlementAmount,
                        credit: 0,
                    },
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.serviceChargeDebtors,
                        category: 'Service Charge Debtors',
                        debit: 0,
                        credit: settlementAmount,
                    },
                ],
            }, now);
        case 'invoice_receipt':
            // Dr 1900 Bank / Cr 1004 Trade Debtors — NEW money-in leg for standard
            // (non-service-charge) invoices. The receivable was raised on invoice
            // create as Dr 1004 / Cr Income, so this settlement clears the debtor.
            return (0, postingEngine_1.buildPosting)({
                ownerId,
                buildingId,
                date,
                description,
                source,
                createdBy,
                lines: [
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.bank,
                        category: 'Cash at Bank',
                        debit: settlementAmount,
                        credit: 0,
                    },
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.tradeDebtors,
                        category: 'Trade Debtors',
                        debit: 0,
                        credit: settlementAmount,
                    },
                ],
            }, now);
        case 'bill_payment':
            // Dr 5001 Creditors / Cr 1900 Bank — mirrors deployed finance/ledgerPosting.ts
            // bill/invoice-payable leg verbatim.
            return (0, postingEngine_1.buildPosting)({
                ownerId,
                buildingId,
                date,
                description,
                source,
                createdBy,
                lines: [
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.creditors,
                        category: 'Trade Creditors',
                        debit: settlementAmount,
                        credit: 0,
                    },
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.bank,
                        category: 'Cash at Bank',
                        debit: 0,
                        credit: settlementAmount,
                    },
                ],
            }, now);
        case 'transfer': {
            const destNominal = context?.transferDestinationNominal ?? controlAccounts_1.CONTROL_ACCOUNTS.bank;
            return (0, postingEngine_1.buildPosting)({
                ownerId,
                buildingId,
                date,
                description,
                source,
                createdBy,
                lines: [
                    {
                        nominalCode: destNominal,
                        category: 'Cash at Bank (destination)',
                        debit: settlementAmount,
                        credit: 0,
                    },
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.bank,
                        category: 'Cash at Bank (source)',
                        debit: 0,
                        credit: settlementAmount,
                    },
                ],
            }, now);
        }
        case 'other_journal': {
            const chosenNominal = context?.otherJournalNominal;
            if (!chosenNominal) {
                throw new ReconciliationError({
                    kind: 'EMPTY_ALLOCATION',
                    message: `other_journal requires context.otherJournalNominal`,
                });
            }
            const direction = context?.otherJournalDirection ?? 'money_in';
            const bankSide = direction === 'money_in'
                ? { debit: settlementAmount, credit: 0 }
                : { debit: 0, credit: settlementAmount };
            const chosenSide = direction === 'money_in'
                ? { debit: 0, credit: settlementAmount }
                : { debit: settlementAmount, credit: 0 };
            return (0, postingEngine_1.buildPosting)({
                ownerId,
                buildingId,
                date,
                description,
                source,
                createdBy,
                lines: [
                    {
                        nominalCode: controlAccounts_1.CONTROL_ACCOUNTS.bank,
                        category: 'Cash at Bank',
                        ...bankSide,
                    },
                    {
                        nominalCode: chosenNominal,
                        ...chosenSide,
                    },
                ],
            }, now);
        }
    }
}
exports.buildSettlementPosting = buildSettlementPosting;
// ============================================================================
// planAllocation
// ============================================================================
/**
 * Compose an `AllocationPlan` from a validated allocation vector. Throws
 * `ReconciliationError` on any validation failure (caller catches, adapts to
 * HTTP 400). Produces one balanced Settlement `BuiltPosting` per allocation,
 * plus the per-item `amountPaid` deltas and link-field selection.
 *
 * `Matched` iff `allocatedAmount === transactionAmount`.
 * `Partial` iff `0 < allocatedAmount < transactionAmount`.
 * `Needs_Explaining` iff `allocatedAmount === 0` (unreachable given
 * `EMPTY_ALLOCATION` rejection, but the status derivation is symmetric).
 *
 * `reconciled === (status === 'Matched')` — Property 1.
 */
function planAllocation(input) {
    const validation = validateAllocation({
        transactionAmount: input.transactionAmount,
        bankAccount: input.bankAccount,
        items: input.items,
        allocations: input.allocations,
    });
    if (!validation.ok)
        throw new ReconciliationError(validation.error);
    const itemById = new Map();
    for (const item of input.items)
        itemById.set(item.itemId, item);
    const plannedAllocations = [];
    const postings = [];
    for (const a of input.allocations) {
        // validateAllocation guarantees item exists.
        const item = itemById.get(a.itemId);
        const newAmountPaid = item.amountPaid + a.amount;
        plannedAllocations.push({
            itemId: a.itemId,
            amount: a.amount,
            newAmountPaid,
            fullyPaid: newAmountPaid >= item.amount,
            linkField: linkFieldForItemType(item.itemType),
        });
        postings.push(buildSettlementPosting({
            txnType: input.txnType,
            itemId: a.itemId,
            ownerId: input.ownerId,
            buildingId: input.bankAccount.buildingId,
            date: input.date,
            bankTransactionId: input.bankTransactionId,
            settlementAmount: a.amount,
            createdBy: input.createdBy,
            context: input.context,
        }, input.now));
    }
    const allocatedAmount = plannedAllocations.reduce((s, p) => s + p.amount, 0);
    const remainingToExplain = input.transactionAmount - allocatedAmount;
    let status;
    if (allocatedAmount === input.transactionAmount)
        status = 'Matched';
    else if (allocatedAmount > 0)
        status = 'Partial';
    else
        status = 'Needs_Explaining';
    return {
        bankTransactionId: input.bankTransactionId,
        transactionAmount: input.transactionAmount,
        allocatedAmount,
        remainingToExplain,
        status,
        reconciled: status === 'Matched',
        allocations: plannedAllocations,
        postings,
    };
}
exports.planAllocation = planAllocation;
/**
 * Convenience wrapper for the unmatch handler: given the original posting a
 * previous match wrote, produce its reversal. Delegates to the shared engine
 * (`buildReversal` swaps debit/credit legs and derives a `reversal#{postingId}`
 * group so the reversal is itself idempotent, guaranteeing net-zero balance).
 *
 * `date` is the effective date the reversal should carry (usually today, so it
 * lands in the current period). `reason` is a human-readable description used
 * for the reversal row's `description`; defaults to `Reversal of {postingId}`.
 */
function buildSettlementReversal(input) {
    return (0, postingEngine_1.buildReversal)(input.originalPosting, {
        date: input.date,
        description: input.reason,
        createdBy: input.createdBy,
    }, input.now);
}
exports.buildSettlementReversal = buildSettlementReversal;
